/**
 * Package Bundler Service
 *
 * Produces the export artifacts:
 *   - the course-bundle zip (the standalone player package the deploy pathway
 *     hosts; runtime identity is injected server-side per scope at deploy time)
 *   - the thin SCORM wrapper zip (`buildScormZipBlob`) uploaded to an LMS, with
 *     rxd/configuration.js pointing at the hosted package URL
 *
 * The legacy combined export (`includeSCORM` — one outer zip holding a
 * client-built course package with a baked SCORM runtime plus its wrapper;
 * jc/package_export, squash-merged as 9f9563b) was REMOVED with NA-361: it
 * predated client-scoped program shells, so a package it built would have
 * launched untracked. Restoring anything like it must resolve the
 * client-scoped program id through the deployment path.
 */

import JSZip from 'jszip';
import {
  DEFAULT_CONTENT_LOCALE,
  type CoursePointer,
  type ExportedCourse,
  type CourseManifest,
  type AssetManifest,
  type AssetEntry,
  type ExportConfig,
  type ScormPackageConfig,
  type ScormVersion,
} from '@course-authoring/shared-types';
import {
  getScormTemplate,
  renderScormConfiguration,
  renderScormManifest,
  renderScormStaticFile,
  validateScormConfig,
} from './scormTemplates';

import { getPlayerBundle as getCompiledPlayerBundle } from './playerBundle';
import rusticiDriverScorm12 from '../../scorm-templates/runtime-rxd/rustici-driver-scorm12.min.js?raw';
import rusticiDriverScorm2004_3rd from '../../scorm-templates/runtime-rxd/rustici-driver-scorm2004_3rd.min.js?raw';
import rusticiXdPkg from '../../scorm-templates/runtime-rxd/rustici-xd-pkg.min.js?raw';
import rusticiXdPkgToLms from '../../scorm-templates/runtime-rxd/rustici-xd-pkg-to-lms.min.js?raw';
import rusticiXdPkgApi from '../../scorm-templates/runtime-rxd/rustici-xd-pkg-api.min.js?raw';
import contentApiHtml from '../../scorm-templates/runtime-rxd/contentAPI.html?raw';

export interface PlayerBundleAsset {
  sourceUrl: string;
  outputPath: string;
}

export interface PlayerBundle {
  js: string;
  css: string;
  assets?: PlayerBundleAsset[];
}

const PACKAGE_LOCAL_RUSTICI_FILES: ReadonlyArray<readonly [string, string]> = [
  // Co-host every supported SCORM driver so a deployed package can serve a
  // wrapper of any version. The wrapper's configuration.js picks the right one
  // via RXDConfig.driverFile (SCORM 2004 4th reuses the 2004 3rd driver).
  ['scorm-rustici/rxd/rustici-driver-scorm12.min.js', rusticiDriverScorm12],
  ['scorm-rustici/rxd/rustici-driver-scorm2004_3rd.min.js', rusticiDriverScorm2004_3rd],
  ['scorm-rustici/rxd/rustici-xd-pkg.min.js', rusticiXdPkg],
  ['scorm-rustici/rxd/rustici-xd-pkg-to-lms.min.js', rusticiXdPkgToLms],
  ['scorm-rustici/rxd/rustici-xd-pkg-api.min.js', rusticiXdPkgApi],
  ['scorm-rustici/rxd/contentAPI.html', contentApiHtml],
] as const;

function getDefaultPlayerBundle(): PlayerBundle {
  return getCompiledPlayerBundle();
}

// ─── Course-bundle index.html ────────────────────────────────────────────────

/**
 * Generates the index.html entry point for the course bundle.
 *
 * Deliberately carries NO `__SCORM_RUNTIME__`: the deploy pathway injects each
 * scope's runtime identity server-side (author-api packageHosting) when the
 * package is hosted, and the SCORM wrapper forwards API_KEY/INT_KEY on the
 * launch URL for that injected script to read.
 */
function generateIndexHtml(manifest: CourseManifest): string {
  const title = escapeHtml(manifest.title);
  const description = escapeHtml(manifest.description);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="description" content="${description}">
  <title>${title}</title>
  <style>
    html, body, #root {
      width: 100%;
      height: 100%;
      margin: 0;
      padding: 0;
    }
    body {
      overflow: hidden;
    }
  </style>
  <link rel="stylesheet" href="player/player.css">
</head>
<body>
  <div id="root"></div>
  <script>
    window.__COURSE_DATA_URL__ = 'data/course.json';
    window.__MANIFEST_URL__ = 'manifest.json';
  </script>
  <script type="module" src="player/player.js"></script>
</body>
</html>`;
}

function escapeHtml(text: string): string {
  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return text.replace(/[&<>"']/g, (char) => htmlEscapes[char] || char);
}

// ─── README generators ───────────────────────────────────────────────────────

function generateCourseReadme(manifest: CourseManifest): string {
  const sourceLine =
    manifest.source === 'published'
      ? `**Source:** Published (${manifest.lessonVersions.length} lesson version${manifest.lessonVersions.length === 1 ? '' : 's'})`
      : '**Source:** Draft (dev snapshot)';

  return `# ${manifest.title}

## Course Package

${sourceLine}
**Exported:** ${new Date(manifest.exportedAt).toLocaleString()}
**Content Hash:** ${manifest.contentHash}

This is the standalone course package. Deploy it to a static web host
(e.g. S3 + CloudFront, an internal CDN, or any plain web server) so that
\`index.html\` is reachable at a stable URL. That URL is the one you typed
into the export dialog as the "Course Package URL"; the SCORM zip's
\`rxd/configuration.js\` points at it.

## Package contents

- \`index.html\` — Player host page; reads SCORM credentials from the URL query string when launched
- \`manifest.json\` — Course metadata
- \`data/course.json\` — Course content
- \`assets/manifest.json\` — Asset reference manifest; course media stays at immutable external URLs
- \`player/\` — Compiled runtime

## Deployment

1. Extract this zip to your static host's content root.
2. Confirm \`https://<your-host>/<path>/index.html\` loads in a browser.
3. The URL you confirm here MUST match the "Course Package URL" you used
   when generating the SCORM zip — otherwise the SCORM launch will fail.

Course media remains linked to immutable external URLs in \`data/course.json\`.
Confirm that your S3/CDN asset host allows learner browsers to load images,
videos, captions, and HLS manifests from the deployed course origin.

**Note:** Opening \`index.html\` directly from the file system will not work
(CORS blocks the course-data fetch). Always serve over HTTP(S).
`;
}

// ─── Course-bundle assembly (the standalone player package) ──────────────────

async function addPlayerBundleAssets(
  zip: JSZip,
  bundle: PlayerBundle
): Promise<void> {
  if (!bundle.assets || bundle.assets.length === 0) return;

  await Promise.all(
    bundle.assets.map(async (asset) => {
      const response = await fetch(asset.sourceUrl);
      if (!response.ok) {
        throw new Error(`Failed to load player asset: ${asset.outputPath}`);
      }
      const assetData = await response.arrayBuffer();
      zip.file(asset.outputPath, assetData);
    })
  );
}

function addPackageLocalRusticiRuntime(zip: JSZip): void {
  for (const [path, body] of PACKAGE_LOCAL_RUSTICI_FILES) {
    zip.file(path, body);
  }
}

function rewriteAssetUrls(
  course: ExportedCourse,
  urlMapper: (url: string) => string | undefined
): ExportedCourse {
  const rewriteValue = (value: unknown): unknown => {
    if (typeof value === 'string') return urlMapper(value) ?? value;
    if (Array.isArray(value)) return value.map(rewriteValue);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, nested]) => [key, rewriteValue(nested)])
      );
    }
    return value;
  };
  return rewriteValue(course) as ExportedCourse;
}

function normalizeLocaleForFile(locale: string | undefined): string {
  const normalized = (locale || DEFAULT_CONTENT_LOCALE).trim().replace(/_/g, '-').toLowerCase();
  return /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/.test(normalized)
    ? normalized
    : DEFAULT_CONTENT_LOCALE;
}

function collectLocalizedCourses(primaryCourse: ExportedCourse, localizedCourses?: readonly ExportedCourse[]): ExportedCourse[] {
  const byLocale = new Map<string, ExportedCourse>();
  const addCourse = (course: ExportedCourse) => {
    byLocale.set(normalizeLocaleForFile(course.locale ?? course.defaultLocale), course);
  };
  addCourse(primaryCourse);
  for (const course of localizedCourses ?? []) {
    addCourse(course);
  }
  return Array.from(byLocale.values());
}

function joinBaseUrl(baseUrl: string, originalUrl: string): string {
  try {
    const original = new URL(originalUrl);
    const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    return new URL(original.pathname.replace(/^\//, ''), base).toString();
  } catch {
    const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    return `${base}${originalUrl.replace(/^https?:\/\/[^/]+\//, '')}`;
  }
}

const LESSON_COMPLETE_PRESET_ASSET_NAMES = new Set([
  'celebration',
  'highfive',
  'ribbon',
  'trophy',
  'wreath',
]);

function normalizeLessonCompletePresetImageUrl(url: string): string | undefined {
  const filename = url.split(/[?#]/, 1)[0]?.split('/').pop();
  if (!filename) return undefined;
  const match = filename.match(/^([a-z]+)(?:-[A-Za-z0-9_-]+)?\.png$/);
  const assetName = match?.[1];
  if (!assetName || !LESSON_COMPLETE_PRESET_ASSET_NAMES.has(assetName)) {
    return undefined;
  }
  return `assets/${assetName}.png`;
}

function rewriteLessonCompletePresetImages(course: ExportedCourse): ExportedCourse {
  return {
    ...course,
    blocks: course.blocks.map((block) => {
      if (block.type !== 'lesson-complete' || !block.imageUrl || block.imageLibraryItemId) {
        return block;
      }
      const normalizedUrl = normalizeLessonCompletePresetImageUrl(block.imageUrl);
      return normalizedUrl ? { ...block, imageUrl: normalizedUrl } : block;
    }),
  };
}

/**
 * Builds the course-bundle zip blob — the standalone player package.
 * This is what gets deployed to a static host; the SCORM package's
 * `contentUrl` opens its `index.html`.
 */
export interface BuildCourseZipOptions {
  /**
   * Force the package-local Rustici runtime into the bundle even when the
   * config is non-SCORM. Used by the automated deploy pathway: the deployed
   * package co-hosts `scorm-rustici/rxd/` same-origin with the player, so the
   * SCORM wrapper's `rxdHostUrl` resolves package-local with no extra hosting.
   */
  forceRusticiRuntime?: boolean;
  /** Additional locale-resolved payloads to include as `data/course.<locale>.json`. */
  localizedCourses?: readonly ExportedCourse[];
}

export async function buildCourseZipBlob(
  course: ExportedCourse,
  manifest: CourseManifest,
  assetManifest: AssetManifest,
  config: ExportConfig,
  playerBundle: PlayerBundle,
  readmeOverride?: string,
  options?: BuildCourseZipOptions
): Promise<Blob> {
  const zip = new JSZip();
  const knownAssetUrls = new Set(assetManifest.assets.map((asset) => asset.url));

  const rewriteCourse = (sourceCourse: ExportedCourse) =>
    rewriteLessonCompletePresetImages(
      rewriteAssetUrls(sourceCourse, (url) => {
        if (config.assetBaseUrl && knownAssetUrls.has(url)) {
          return joinBaseUrl(config.assetBaseUrl, url);
        }
        return undefined;
      })
    );
  const localizedCourses = collectLocalizedCourses(course, options?.localizedCourses).map(rewriteCourse);
  const defaultLocale = normalizeLocaleForFile(manifest.defaultLocale ?? course.defaultLocale ?? course.locale);
  const defaultCourse =
    localizedCourses.find((localizedCourse) => normalizeLocaleForFile(localizedCourse.locale) === defaultLocale) ??
    localizedCourses[0] ??
    rewriteCourse(course);

  const enrichedAssets: AssetEntry[] = assetManifest.assets.map((asset) => ({
    id: asset.id,
    url: asset.url,
    contentType: asset.contentType,
    category: asset.category,
    referencedBy: asset.referencedBy,
    bundled: false,
  }));
  const enrichedManifest: AssetManifest = { ...assetManifest, assets: enrichedAssets };

  zip.file('index.html', generateIndexHtml(manifest));
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));
  zip.file('README.md', readmeOverride ?? generateCourseReadme(manifest));
  zip.file('data/course.json', JSON.stringify(defaultCourse, null, 2));
  for (const localizedCourse of localizedCourses) {
    const locale = normalizeLocaleForFile(localizedCourse.locale ?? localizedCourse.defaultLocale);
    zip.file(`data/course.${locale}.json`, JSON.stringify(localizedCourse, null, 2));
  }
  zip.file('assets/manifest.json', JSON.stringify(enrichedManifest, null, 2));
  zip.file('player/player.js', playerBundle.js);
  zip.file('player/player.css', playerBundle.css);
  if (options?.forceRusticiRuntime) {
    // Co-host Rustici's contentAPI.html beside the player so `window.parent.RXD`
    // is same-origin: the automated deploy pathway forces it so the hosted
    // package is self-contained and the wrapper's rxdHostUrl resolves
    // package-local.
    addPackageLocalRusticiRuntime(zip);
  }
  await addPlayerBundleAssets(zip, playerBundle);

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/**
 * Builds the course-POINTER package zip (referenced-lesson-packages): course
 * metadata + root-relative lessonRefs per locale, the player bundle, and the
 * package-local Rustici runtime — no embedded lesson content. No manifest.json
 * (the deploy endpoint reads title/description from data/course.json) and no
 * index.html (regenerated per account scope server-side).
 */
export async function buildCoursePointerZipBlob(
  pointers: readonly CoursePointer[],
  playerBundle?: PlayerBundle,
): Promise<Blob> {
  const defaultPointer =
    pointers.find(
      (pointer) =>
        normalizeLocaleForFile(pointer.locale) ===
        normalizeLocaleForFile(pointer.defaultLocale ?? pointer.locale)
    ) ?? pointers[0];
  if (!defaultPointer) {
    throw new Error('Cannot build a course pointer package without at least one locale payload.');
  }

  const zip = new JSZip();
  const bundle = playerBundle || getDefaultPlayerBundle();

  zip.file('data/course.json', JSON.stringify(defaultPointer, null, 2));
  for (const pointer of pointers) {
    const locale = normalizeLocaleForFile(pointer.locale ?? pointer.defaultLocale);
    zip.file(`data/course.${locale}.json`, JSON.stringify(pointer, null, 2));
  }
  zip.file('player/player.js', bundle.js);
  zip.file('player/player.css', bundle.css);
  // Deployed packages always co-host the Rustici runtime (see forceRusticiRuntime).
  addPackageLocalRusticiRuntime(zip);
  await addPlayerBundleAssets(zip, bundle);

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

// ─── Questionnaire content package (NA-256) ──────────────────────────────────

/**
 * Builds the questionnaire content package: the baked decision tree
 * (`data/questionnaire.json`, produced by author-api) plus the same player
 * bundle and package-local Rustici runtime a course package carries.
 *
 * The package deliberately has no `data/course.json`, no asset manifest and no
 * locale variants — the questionnaire's terminals point at whole course
 * packages, which carry their own content. No `index.html` either: the deploy
 * endpoint regenerates it with the account's runtime, exactly as it does for
 * course packages.
 *
 * `doc` is passed through opaquely (the server owns that contract), so a field
 * added server-side reaches the player without a change here.
 */
export async function buildQuestionnairePackageZipBlob(
  documents: Record<string, unknown>,
  playerBundle?: PlayerBundle,
): Promise<Blob> {
  const zip = new JSZip();
  const bundle = playerBundle || getDefaultPlayerBundle();

  // One file per locale, at the paths author-api chose — the default locale keeps the
  // unsuffixed `data/questionnaire.json` so shells already in an LMS resolve unchanged,
  // and each translated locale lands beside it as `questionnaire.<locale>.json`.
  //
  // The paths are the map's keys rather than derived here: author-api owns the layout
  // alongside the document shape, and deriving them a second time is how the two would
  // drift. It refuses a package whose advertised locales lack files, so an empty or
  // mis-keyed map fails the publish rather than shipping a switcher that 404s.
  for (const [path, document] of Object.entries(documents)) {
    zip.file(path, JSON.stringify(document, null, 2));
  }
  zip.file('player/player.js', bundle.js);
  zip.file('player/player.css', bundle.css);
  // Deployed packages always co-host the Rustici runtime so the shell's
  // rxdHostUrl resolves package-local (see forceRusticiRuntime).
  addPackageLocalRusticiRuntime(zip);
  await addPlayerBundleAssets(zip, bundle);

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

// ─── SCORM-zip assembly (the thin LMS-side wrapper) ──────────────────────────

export interface BuildScormZipInput {
  courseId: string;
  courseTitle: string;
  scormVersion: ScormVersion;
  scorm: ScormPackageConfig;
}

/**
 * Builds the SCORM-zip blob — the thin RXD shell uploaded to the LMS.
 * Contains only the SCORM SCO files; the player and course content live in
 * the separate course package reached via `RXDConfig.contentUrl`.
 */
export async function buildScormZipBlob(input: BuildScormZipInput): Promise<Blob> {
  const missing = validateScormConfig(input.scorm);
  if (missing) {
    throw new Error(`SCORM_CONFIG_INVALID: missing required fields: ${missing.join(', ')}`);
  }

  const zip = new JSZip();
  const template = getScormTemplate(input.scormVersion);

  for (const [path, body] of Object.entries(template.staticFiles)) {
    zip.file(path, renderScormStaticFile({ body, courseTitle: input.courseTitle }));
  }
  // Schema files ship verbatim (never templated) and stay out of the manifest
  // <file> list — the shape strict LMS importers (Sana) validated.
  for (const [path, body] of Object.entries(template.schemaFiles)) {
    zip.file(path, body);
  }
  zip.file(
    'rxd/configuration.js',
    renderScormConfiguration({
      scorm: input.scorm,
      courseTitle: input.courseTitle,
      version: input.scormVersion,
    })
  );
  zip.file(
    'imsmanifest.xml',
    renderScormManifest({
      version: input.scormVersion,
      courseId: input.courseId,
      courseTitle: input.courseTitle,
    })
  );

  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

// ─── Public entry ────────────────────────────────────────────────────────────

/**
 * Creates the export package: the standalone course-bundle zip.
 *
 * (The legacy `includeSCORM` combined export that used to branch here was
 * removed — see the file header comment.)
 */
export async function createExportZip(
  course: ExportedCourse,
  manifest: CourseManifest,
  assetManifest: AssetManifest,
  config: ExportConfig,
  playerBundle?: PlayerBundle,
  options?: BuildCourseZipOptions
): Promise<Blob> {
  const bundle = playerBundle || getDefaultPlayerBundle();
  return buildCourseZipBlob(course, manifest, assetManifest, config, bundle, undefined, options);
}
